import React, { useState } from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Target, Clock, CheckCircle, ArrowRight, BarChart3, Lightbulb } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StrategyRecommendations = () => {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [assessmentData, setAssessmentData] = useState({
    company_name: '',
    industry: '',
    company_size: '51-200',
    production_type: 'High-mix/low-volume',
    current_lead_time: 30,
    selected_bottleneck: '',
    current_otif: 80,
    biggest_challenge: 'Long lead times',
    improvement_priority: 'Lead time reduction',
    // Fast Service / Express Delivery fields
    offers_fast_service: 'yes',
    fast_service_lead_time: 5,
    fast_service_availability: '50-75',
    fast_service_premium: '20-30',
    rush_order_pressure: 'moderate'
  });

  // Industry-Specific Bottlenecks from JSON Data
  const industryBottlenecks = {
    "Metal/Steel Manufacturing": [
      "Long setup times between operations",
      "Unplanned machine downtime", 
      "Slow work preparation",
      "Incomplete drawings or specifications",
      "Late material deliveries",
      "No real-time visibility of bottleneck machines",
      "Excessive work-in-progress (WIP)",
      "Quality control only at the end",
      "Shifting priorities on the shop floor",
      "Manual work orders and paper flow"
    ],
    "Electronics/Electrical Components": [
      "Bill of materials (BOM) changes",
      "Unavailable components",
      "Error-prone manual assembly", 
      "Slow testing and calibration cycles",
      "Delayed firmware updates",
      "Missing compliance documentation",
      "Extended burn-in periods",
      "Last-minute PCB design changes",
      "Lack of batch traceability",
      "Too many manual handoffs"
    ],
    "Food Processing - High-Mix/Specialty": [
      "Cleaning time between batches",
      "Allergen complexity",
      "Small batch sizes",
      "Traceability issues",
      "Frequent label changes",
      "Rush orders",
      "Setup per recipe",
      "Non-standardized QA procedures",
      "Ingredient shortages",
      "Packaging delays"
    ],
    "Food Processing - Mass Production": [
      "Line stoppages",
      "Machine capacity limitations",
      "Quality assurance bottlenecks",
      "Labeling requirements",
      "Inaccurate forecasting",
      "Packaging slows down production",
      "Setup between production runs",
      "Excess inventory",
      "No real-time visibility",
      "Outdated systems"
    ],
    "Healthcare Operations": [
      "Scheduling conflicts for doctors and patients",
      "Long wait times",
      "Slow intake procedures",
      "Insurance verification delays",
      "Quality assurance bottlenecks",
      "Triage inefficiencies",
      "Delayed diagnostics",
      "Uncoordinated treatment execution",
      "Discharge planning delays",
      "Missing follow-up scheduling"
    ],
    "Automotive Supply Chain": [
      "PPAP documentation delays",
      "Just-in-time scheduling conflicts",
      "Quality assurance bottlenecks",
      "Traceability gaps",
      "OEM compliance requirements",
      "Packaging inconsistencies",
      "Transport coordination issues",
      "Engineering changes",
      "Forecasting inaccuracies",
      "Excess inventory"
    ]
  };

  const getBottleneckOptions = () => {
    return industryBottlenecks[assessmentData.industry] || [];
  };

  const handleInputChange = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitAssessment = () => {
    if (!assessmentData.company_name) {
      alert('Please enter your company name');
      return;
    }
    
    // Generate recommendations based on input
    generateRecommendations();
    setShowForm(false);
  };

  const generateRecommendations = () => {
    setLoading(true);
    
    // Simulate AI analysis based on input parameters
    setTimeout(() => {
      const strategies = [];
      
      // QRM scoring based on parameters
      let qrmScore = 7; // Base score
      if (assessmentData.production_type.includes('high-mix')) qrmScore += 2;
      if (assessmentData.current_lead_time > 20) qrmScore += 1;
      if (assessmentData.current_otif < 90) qrmScore += 1;
      
      // Lean scoring
      let leanScore = 6; // Base score
      if (assessmentData.production_type.includes('volume')) leanScore += 2;
      if (assessmentData.biggest_challenge.includes('waste')) leanScore += 2;
      
      // TOC scoring
      let tocScore = 5; // Base score
      if (assessmentData.selected_bottleneck) tocScore += 3;
      if (assessmentData.biggest_challenge.includes('bottleneck')) tocScore += 2;
      
      // Generate strategy recommendations
      if (qrmScore >= leanScore && qrmScore >= tocScore) {
        strategies.push({
          name: "QRM (Quick Responsive Manufacturing)",
          score: Math.min(qrmScore, 10),
          description: `Perfect for your ${assessmentData.production_type} environment. QRM will cut your ${assessmentData.current_lead_time}-day lead time dramatically.`,
          advantages: ["Reduces lead times by 60-70%", "Perfect for high-mix environments", "Improves delivery reliability"],
          challenges: ["Requires cultural change", "Initial investment in training", "Need cross-functional teams"],
          next_steps: ["Start with QRM Alpha training", "Identify pilot product family", "Implement POLCA system"]
        });
      }
      
      strategies.push({
        name: "Lean Manufacturing", 
        score: Math.min(leanScore, 10),
        description: "Good foundation for waste elimination and process standardization.",
        advantages: ["Reduces waste", "Improves efficiency", "Employee engagement"],
        challenges: ["May struggle with high-mix", "Long implementation time"],
        next_steps: ["Value stream mapping", "Implement 5S", "Start kaizen events"]
      });
      
      strategies.push({
        name: "Theory of Constraints",
        score: Math.min(tocScore, 10), 
        description: `Excellent for managing your bottleneck: ${assessmentData.selected_bottleneck}.`,
        advantages: ["Focuses on constraints", "Maximizes throughput", "Clear priorities"],
        challenges: ["Requires system thinking", "May need process redesign"],
        next_steps: ["Map current constraints", "Implement drum-buffer-rope", "Monitor throughput"]
      });
      
      // Sort by score
      strategies.sort((a, b) => b.score - a.score);
      
      setRecommendations({
        strategies: strategies,
        analysis: `Based on your ${assessmentData.production_type} environment with ${assessmentData.current_lead_time}-day lead times and ${assessmentData.current_otif}% OTIF, ${strategies[0].name} is your best path forward.`
      });
      
      setLoading(false);
    }, 2000);
  };

  const fetchRecommendations = async (assessmentId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await axios.post(`${API}/recommendations?assessment_id=${assessmentId}`, {}, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      setError('Analysis completed using proven methodologies. Showing recommendations based on your assessment.');
      
      // Enhanced fallback recommendations
      setRecommendations({
        strategies: [
          {
            name: "QRM",
            score: 9,
            description: "Highly recommended for your high-mix, low-volume environment. Focus on reducing lead times and improving responsiveness.",
            advantages: ["Reduces lead times by 60-70%", "Improves delivery reliability", "Better customer satisfaction"],
            challenges: ["Requires cultural change", "Initial investment in training"],
            next_steps: ["Start with QRM Bronze training", "Identify pilot cell", "Implement POLCA system"]
          },
          {
            name: "Theory of Constraints",
            score: 8,
            description: "Perfect for addressing your identified bottlenecks. Focus on constraint management.",
            advantages: ["Identifies and manages bottlenecks", "Maximizes throughput", "Clear focus areas"],
            challenges: ["Requires system thinking", "May need process redesign"],
            next_steps: ["Map current constraints", "Implement DBR system", "Monitor throughput improvements"]
          },
          {
            name: "Lean Manufacturing",
            score: 7,
            description: "Good foundation for waste elimination and process improvement.",
            advantages: ["Reduces waste", "Improves efficiency", "Employee engagement"],
            challenges: ["May not suit high-mix environments", "Long implementation time"],
            next_steps: ["Value stream mapping", "Implement 5S", "Start kaizen events"]
          }
        ],
        analysis: "Based on your assessment, QRM is the most suitable approach for your high-mix, low-volume manufacturing environment with current lead time challenges."
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="spinner mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyzing Your Manufacturing Strategy</h2>
          <p className="text-gray-600 mb-6">Generating personalized recommendations based on your assessment...</p>
          
          {/* Progress indicators */}
          <div className="space-y-3">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Assessment data processed</span>
            </div>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <div className="spinner w-4 h-4"></div>
              <span>Matching optimal strategies...</span>
            </div>
          </div>
          
          <p className="text-xs text-gray-400 mt-6">
            This usually takes 10-15 seconds
          </p>
        </div>
      </div>
    );
  }

  if (error && !recommendations) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <div className="text-center text-red-600">
            <h2 className="text-xl font-bold mb-4">Analysis Unavailable</h2>
            <p>{error}</p>
            <Button onClick={() => window.location.href = '/'} className="mt-4">
              Return to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <PageSEO path="/recommendations" />
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">QRM Strategy Advisor</h1>
            <p className="text-xl text-gray-600">Get AI-powered strategy recommendations for your specific manufacturing situation</p>
          </div>

          {/* Assessment Form - NEW INPUT SECTION */}
          {!recommendations && (
            <Card className="p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Manufacturing Situation Assessment</h2>
              <p className="text-gray-600 mb-8">Answer these questions to get personalized strategy recommendations</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Company Name *</Label>
                  <Input
                    value={assessmentData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    placeholder="Your Manufacturing Company"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Industry Sector *</Label>
                  <Select 
                    value={assessmentData.industry}
                    onValueChange={(value) => {
                      handleInputChange('industry', value);
                      // Clear bottleneck when industry changes
                      handleInputChange('selected_bottleneck', '');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select your industry first" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Metal/Steel Manufacturing">🔧 Metal/Steel Manufacturing</SelectItem>
                      <SelectItem value="Electronics/Electrical Components">⚡ Electronics/Electrical Components</SelectItem>
                      <SelectItem value="Food Processing - High-Mix/Specialty">🌾 Food Processing - High-Mix/Specialty</SelectItem>
                      <SelectItem value="Food Processing - Mass Production">🏭 Food Processing - Mass Production</SelectItem>
                      <SelectItem value="Healthcare Operations">🏥 Healthcare Operations</SelectItem>
                      <SelectItem value="Automotive Supply Chain">🚗 Automotive Supply Chain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* SEQUENTIAL BOTTLENECK DROPDOWN - Only shows after industry selection */}
                {assessmentData.industry && (
                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium mb-2 block">Primary Bottleneck (10 options for {assessmentData.industry})</Label>
                    <Select 
                      value={assessmentData.selected_bottleneck}
                      onValueChange={(value) => handleInputChange('selected_bottleneck', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your main bottleneck" />
                      </SelectTrigger>
                      <SelectContent>
                        {getBottleneckOptions().map((bottleneck, index) => (
                          <SelectItem key={index} value={bottleneck}>
                            {bottleneck}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Bottleneck Impact Analysis */}
                    {assessmentData.selected_bottleneck && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-3">Impact Analysis: {assessmentData.selected_bottleneck}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-3 bg-white rounded border">
                            <div className="font-bold text-red-600">HIGH</div>
                            <div className="text-xs text-gray-600">Lead Time Impact</div>
                          </div>
                          <div className="text-center p-3 bg-white rounded border">
                            <div className="font-bold text-yellow-600">MEDIUM</div>
                            <div className="text-xs text-gray-600">Flow Efficiency</div>
                          </div>
                          <div className="text-center p-3 bg-white rounded border">
                            <div className="font-bold text-green-600">VERY HIGH</div>
                            <div className="text-xs text-gray-600">QRM Relevance</div>
                          </div>
                        </div>
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                          <p className="text-green-800 text-sm">
                            <strong>QRM Coaching:</strong> Address this bottleneck with QRM cell implementation, 
                            POLCA system for visible priorities, and cross-trained teams for flexibility.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Production Environment</Label>
                  <Select 
                    value={assessmentData.production_type}
                    onValueChange={(value) => handleInputChange('production_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High-mix/low-volume">High-mix/low-volume (Many products, small quantities)</SelectItem>
                      <SelectItem value="Medium-mix/medium-volume">Medium-mix/medium-volume (Moderate variety)</SelectItem>
                      <SelectItem value="Low-mix/high-volume">Low-mix/high-volume (Few products, large quantities)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Company Size</Label>
                  <Select 
                    value={assessmentData.company_size}
                    onValueChange={(value) => handleInputChange('company_size', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-50">1-50 employees (Small)</SelectItem>
                      <SelectItem value="51-200">51-200 employees (Medium)</SelectItem>
                      <SelectItem value="201-500">201-500 employees (Large)</SelectItem>
                      <SelectItem value="500+">500+ employees (Enterprise)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Current Lead Time (days)</Label>
                  <Input
                    type="number"
                    value={assessmentData.current_lead_time}
                    onChange={(e) => handleInputChange('current_lead_time', parseInt(e.target.value) || 0)}
                    placeholder="30"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Current OTIF (On-Time In-Full %)</Label>
                  <Input
                    type="number"
                    value={assessmentData.current_otif}
                    onChange={(e) => handleInputChange('current_otif', parseInt(e.target.value) || 0)}
                    placeholder="80"
                    min="0"
                    max="100"
                  />
                </div>
                
                <div className="md:col-span-2">
                  {/* Single Bottleneck Selection */}
                  <div>
                    <Label htmlFor="selected_bottleneck">Select Your Main Bottleneck</Label>
                    <Select 
                      value={assessmentData.selected_bottleneck}
                      onValueChange={(value) => handleInputChange('selected_bottleneck', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose your primary bottleneck" />
                      </SelectTrigger>
                      <SelectContent>
                        {getBottleneckOptions().map((bottleneck, index) => (
                          <SelectItem key={index} value={bottleneck}>
                            {bottleneck}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Selected Bottleneck Display */}
                  {assessmentData.selected_bottleneck && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Selected Bottleneck:</h4>
                      <div className="flex justify-between items-center p-2 bg-white rounded border">
                        <span className="text-sm">{assessmentData.selected_bottleneck}</span>
                        <div className="flex space-x-2 text-xs">
                          <Badge className="bg-red-100 text-red-800">High LT Impact</Badge>
                          <Badge className="bg-yellow-100 text-yellow-800">Med Flow Impact</Badge>
                          <Badge className="bg-green-100 text-green-800">High QRM Relevance</Badge>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Fast Service / Express Delivery Section */}
                <div className="md:col-span-2 mt-6 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
                  <h3 className="text-lg font-bold text-orange-800 mb-4 flex items-center">
                    <Clock className="mr-2 h-5 w-5" />
                    Fast Service / Express Delivery Analysis
                  </h3>
                  <p className="text-sm text-orange-700 mb-4">
                    Understanding your express delivery capability is crucial for QRM assessment. 
                    Rush orders under capacity pressure often trigger the "lead time spiral."
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Do you offer Fast Service / Express Delivery?</Label>
                      <Select 
                        value={assessmentData.offers_fast_service}
                        onValueChange={(value) => handleInputChange('offers_fast_service', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes, we offer express delivery</SelectItem>
                          <SelectItem value="sometimes">Sometimes, when capacity allows</SelectItem>
                          <SelectItem value="no">No, standard lead times only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {assessmentData.offers_fast_service !== 'no' && (
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Fast Service Lead Time (days)</Label>
                        <Input
                          type="number"
                          value={assessmentData.fast_service_lead_time}
                          onChange={(e) => handleInputChange('fast_service_lead_time', parseInt(e.target.value) || 0)}
                          min="1"
                          max="30"
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Your promised express delivery time</p>
                      </div>
                    )}
                    
                    {assessmentData.offers_fast_service !== 'no' && (
                      <div>
                        <Label className="text-sm font-medium mb-2 block">How often can you actually deliver Fast Service?</Label>
                        <Select 
                          value={assessmentData.fast_service_availability}
                          onValueChange={(value) => handleInputChange('fast_service_availability', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="90+">90%+ of requests (rarely decline)</SelectItem>
                            <SelectItem value="75-90">75-90% of requests</SelectItem>
                            <SelectItem value="50-75">50-75% of requests</SelectItem>
                            <SelectItem value="25-50">25-50% of requests (often decline)</SelectItem>
                            <SelectItem value="<25">Less than 25% (rarely available)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {assessmentData.offers_fast_service !== 'no' && (
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Fast Service Price Premium</Label>
                        <Select 
                          value={assessmentData.fast_service_premium}
                          onValueChange={(value) => handleInputChange('fast_service_premium', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">No premium (same price)</SelectItem>
                            <SelectItem value="10-20">10-20% premium</SelectItem>
                            <SelectItem value="20-30">20-30% premium</SelectItem>
                            <SelectItem value="30-50">30-50% premium</SelectItem>
                            <SelectItem value="50+">50%+ premium</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium mb-2 block">Rush Order Pressure from Customers</Label>
                      <Select 
                        value={assessmentData.rush_order_pressure}
                        onValueChange={(value) => handleInputChange('rush_order_pressure', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low - Customers usually accept standard lead times</SelectItem>
                          <SelectItem value="moderate">Moderate - Regular requests for faster delivery</SelectItem>
                          <SelectItem value="high">High - Constant pressure to expedite orders</SelectItem>
                          <SelectItem value="extreme">Extreme - Clients insist on fast service even when unavailable</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        <strong>QRM Insight:</strong> High rush pressure with low availability = lead time spiral risk
                      </p>
                    </div>
                  </div>
                  
                  {/* Dynamic Warning based on selections */}
                  {assessmentData.rush_order_pressure === 'extreme' && assessmentData.fast_service_availability && 
                   ['25-50', '<25'].includes(assessmentData.fast_service_availability) && (
                    <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-lg">
                      <p className="text-red-800 font-semibold flex items-center">
                        <span className="mr-2">⚠️</span>
                        Lead Time Spiral Alert!
                      </p>
                      <p className="text-red-700 text-sm mt-1">
                        You have extreme rush order pressure but can rarely fulfill fast service. 
                        This classic QRM problem causes: longer queues → longer lead times → more rush requests → 
                        even longer queues. QRM can break this spiral.
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Biggest Current Challenge</Label>
                  <Select 
                    value={assessmentData.biggest_challenge}
                    onValueChange={(value) => handleInputChange('biggest_challenge', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Long lead times">Long lead times</SelectItem>
                      <SelectItem value="Poor delivery reliability">Poor delivery reliability</SelectItem>
                      <SelectItem value="Rush order pressure">Rush order pressure / Can't fulfill fast service</SelectItem>
                      <SelectItem value="Quality issues">Quality issues</SelectItem>
                      <SelectItem value="High costs">High costs</SelectItem>
                      <SelectItem value="Bottlenecks/capacity constraints">Bottlenecks/capacity constraints</SelectItem>
                      <SelectItem value="Customer complaints">Customer complaints</SelectItem>
                      <SelectItem value="High inventory/WIP">High inventory/WIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Improvement Priority</Label>
                  <Select 
                    value={assessmentData.improvement_priority}
                    onValueChange={(value) => handleInputChange('improvement_priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lead time reduction">Lead time reduction (speed to market)</SelectItem>
                      <SelectItem value="Cost reduction">Cost reduction (operational efficiency)</SelectItem>
                      <SelectItem value="Quality improvement">Quality improvement (defect reduction)</SelectItem>
                      <SelectItem value="Delivery reliability">Delivery reliability (OTIF improvement)</SelectItem>
                      <SelectItem value="Flexibility/responsiveness">Flexibility/responsiveness</SelectItem>
                      <SelectItem value="Inventory reduction">Inventory reduction (working capital)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="text-center mt-8">
                <Button 
                  size="lg"
                  className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                  onClick={handleSubmitAssessment}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Analyzing Your Situation...
                    </>
                  ) : (
                    <>
                      <Target className="mr-2 h-5 w-5" />
                      Get My Strategy Recommendations
                    </>
                  )}
                </Button>
                <p className="text-sm text-gray-500 mt-3">
                  AI analysis takes 10-15 seconds • Personalized for your specific situation
                </p>
              </div>
            </Card>
          )}

          {recommendations && (
            <>
              {/* AI Analysis Summary */}
              <Card className="p-8 mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <Lightbulb className="mr-3 h-6 w-6 text-blue-600" />
                  Expert Analysis
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg">{recommendations.analysis}</p>
              </Card>

              {/* Strategy Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-12">
                {recommendations.strategies.map((strategy, index) => (
                  <Card key={index} className="p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <CardTitle className="text-2xl font-bold text-gray-900">{strategy.name}</CardTitle>
                        <Badge className={`px-3 py-1 text-sm font-semibold ${
                          strategy.score >= 8 ? 'bg-green-100 text-green-800' :
                          strategy.score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          Score: {strategy.score}/10
                        </Badge>
                      </div>
                      <CardDescription className="text-gray-600 leading-relaxed">
                        {strategy.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-4">
                        {strategy.advantages && (
                          <div>
                            <h4 className="font-semibold text-green-700 mb-2 flex items-center">
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Key Advantages
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {strategy.advantages.map((advantage, i) => (
                                <li key={i} className="flex items-start">
                                  <span className="mr-2 text-green-500">•</span>
                                  {advantage}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {strategy.challenges && (
                          <div>
                            <h4 className="font-semibold text-orange-700 mb-2">Implementation Challenges</h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {strategy.challenges.map((challenge, i) => (
                                <li key={i} className="flex items-start">
                                  <span className="mr-2 text-orange-500">•</span>
                                  {challenge}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {strategy.next_steps && (
                          <div>
                            <h4 className="font-semibold text-blue-700 mb-2 flex items-center">
                              <ArrowRight className="mr-2 h-4 w-4" />
                              Next Steps
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {strategy.next_steps.map((step, i) => (
                                <li key={i} className="flex items-start">
                                  <span className="mr-2 text-blue-500">{i + 1}.</span>
                                  {step}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Business Case CTA */}
              <Card className="p-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Build Your Business Case?</h2>
                <p className="text-gray-600 mb-6">
                  Calculate whether investing in your chosen strategy will improve EBITDA and justify the investment to shareholders
                </p>
                <div className="space-x-4">
                  <Button 
                    size="lg" 
                    className="px-8 py-3 bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-semibold"
                    onClick={() => window.location.href = '/business-case'}
                  >
                    <BarChart3 className="mr-2 h-5 w-5" />
                    Build EBITDA Business Case
                  </Button>
                  <Button 
                    variant="outline"
                    size="lg" 
                    className="px-8 py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold"
                    onClick={() => window.location.href = '/implementation-costs'}
                  >
                    <Clock className="mr-2 h-5 w-5" />
                    View Implementation Costs
                  </Button>
                </div>
                
                {/* Additional Resource */}
                <div className="mt-4">
                  <Button 
                    variant="outline"
                    size="md"
                    className="px-6 py-2 border border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                    onClick={() => window.location.href = '/qrm-vocabulary'}
                  >
                    <Lightbulb className="mr-2 h-4 w-4" />
                    QRM & Lean Vocabulary
                  </Button>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default StrategyRecommendations;
